import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Clock, AlertTriangle, LogOut } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function StatCard({ icon: Icon, label, value, color = "text-foreground" }) {
  return (
    <Card className="border-border/50">
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0">
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <div>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function LiveStatusPanel() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await authAxiosRef.current.get("/timeclock/live-status");
      setStatus(res.data);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  if (loading || !status) return null;

  return (
    <div className="space-y-4" data-testid="live-status-panel">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Users} label="Em serviço agora" value={status.count_on_duty} color="text-emerald-600" />
        <StatCard icon={Clock} label="Horas hoje" value={`${status.total_hours_today}h`} />
        <StatCard icon={AlertTriangle} label="Atrasados" value={status.count_late} color={status.count_late > 0 ? "text-amber-500" : "text-muted-foreground"} />
        <StatCard icon={LogOut} label="Sem saída" value={status.count_missing_checkout} color={status.count_missing_checkout > 0 ? "text-red-500" : "text-muted-foreground"} />
      </div>

      {/* On duty list */}
      {status.on_duty.length > 0 && (
        <Card className="border-border/50">
          <CardContent className="p-0">
            <div className="px-4 py-2 border-b border-border/50">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Em serviço</p>
            </div>
            <div className="divide-y divide-border/30">
              {status.on_duty.map(e => (
                <div key={e.member_id} className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse shrink-0" />
                    <span className="text-sm font-medium">{e.member_name}</span>
                    {(e.flags || []).includes("outside_location") && (
                      <Badge variant="outline" className="text-[10px] text-orange-600 border-orange-300 px-1.5 py-0">Fora do local</Badge>
                    )}
                    {(e.flags || []).includes("gps_low_accuracy") && (
                      <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300 px-1.5 py-0">GPS impreciso</Badge>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Entrada: {fmtTime(e.clock_in)}</p>
                    <p className="text-xs font-bold">{e.hours_open}h</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Late arrivals */}
      {status.late_arrivals.length > 0 && (
        <Card className="border-amber-200">
          <CardContent className="p-0">
            <div className="px-4 py-2 border-b border-amber-200 bg-amber-50 rounded-t-lg">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Atrasos</p>
            </div>
            <div className="divide-y divide-border/30">
              {status.late_arrivals.map(e => (
                <div key={e.member_id} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm font-medium">{e.member_name}</span>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Previsto: {e.expected_at}</p>
                    <p className="text-xs font-bold text-amber-600">{e.minutes_late} min atraso</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Missing checkout */}
      {status.missing_checkout.length > 0 && (
        <Card className="border-red-200">
          <CardContent className="p-0">
            <div className="px-4 py-2 border-b border-red-200 bg-red-50 rounded-t-lg">
              <p className="text-xs font-semibold uppercase tracking-wide text-red-700">Saída não registada</p>
            </div>
            <div className="divide-y divide-border/30">
              {status.missing_checkout.map(e => (
                <div key={e.member_id} className="flex items-center justify-between px-4 py-2.5">
                  <span className="text-sm font-medium">{e.member_name}</span>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Entrada: {fmtTime(e.clock_in)}</p>
                    <p className="text-xs font-bold text-red-600">{e.hours_open}h em aberto</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-[10px] text-muted-foreground text-right">
        Atualizado: {new Date(status.as_of).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} · atualiza a cada min
      </p>
    </div>
  );
}
